import { ChevronDown, Sparkles } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { useCanMarkCaseReviewed } from "../../permissions/v2/cases";

export const statusColors = {
  "-1": "bg-destructive-subtle text-destructive-text", // error
  0: "bg-muted text-secondary-foreground", // draft
  1: "bg-ai-subtle text-ai-text", // ai_review
  2: "bg-info-subtle text-info-text", // human_review
  4: "bg-success-subtle text-success-text", // finished
  6: "border border-border-strong text-muted-foreground", // archived
};

export const statusNames = {
  "-1": "Error",
  0: "Draft",
  1: "AI review",
  2: "Human review",
  4: "Finished",
  6: "Archived",
};

const AIReviewBadge = () => {
  return (
    <div className="relative min-w-[120px] animate-shimmer rounded-full bg-gradient-to-r from-ai/50 via-transparent to-ai/50 bg-[length:400%_100%] p-[2px]">
      <div className="relative inline-flex w-full items-center rounded-full bg-ai-subtle px-3 py-2 text-xs font-medium text-ai-text">
        <Sparkles className="mr-2 h-4 w-4" />
        <span>AI review</span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, onStatusChange, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { hasPermission, firmContext } = useAuth();
  const canMarkReviewed = useCanMarkCaseReviewed(firmContext?.firmId);

  // Check if user can edit status
  const canChangeStatus =
    !readOnly && (hasPermission("case_actions", "update_status") || hasPermission("super_admin"));

  // Don't allow status changes during AI Review unless super admin
  const isEditable = canChangeStatus && (status !== 1 || hasPermission("super_admin"));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusClick = (e) => {
    if (!isEditable) return;
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleStatusChange = (newStatus, e) => {
    e.stopPropagation();
    setIsOpen(false);
    onStatusChange(newStatus);
  };

  // If no onStatusChange provided, render simple badge
  if (!onStatusChange || readOnly) {
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || "bg-surface-sunken"}`}
      >
        {statusNames[status] || "Unknown"}
      </span>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {status === 1 ? (
        <div
          className={`${isEditable ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={handleStatusClick}
        >
          <AIReviewBadge />
        </div>
      ) : (
        <div
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || "bg-surface-sunken"} ${isEditable ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={handleStatusClick}
        >
          <span>{statusNames[status] || "Unknown"}</span>
          {isEditable && <ChevronDown className="ml-1 h-3 w-3" />}
        </div>
      )}

      {isOpen && isEditable && (
        <div
          className="fixed z-popover mt-1 w-40 rounded-md bg-card py-1 shadow-lg ring-1 ring-black ring-opacity-5"
          style={{
            top: dropdownRef.current?.getBoundingClientRect().bottom + 4,
            left: dropdownRef.current?.getBoundingClientRect().left,
          }}
        >
          <div className="py-1" role="menu">
            {Object.entries(statusNames)
              .filter(([id]) => {
                const numId = Number(id);
                // Only super admins can see AI Review in dropdown
                if (numId === 1 && !hasPermission("super_admin")) return false;
                // Only users with specific permissions can move to certain statuses
                if (numId === 4 && !canMarkReviewed) return false;
                return true;
              })
              .map(([id, name]) => (
                <button
                  key={id}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-sm hover:bg-surface-subtle ${Number(id) === status ? "bg-surface-subtle" : ""}`}
                  onClick={(e) => handleStatusChange(Number(id), e)}
                >
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[id]}`}
                  >
                    {name}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.number.isRequired,
  onStatusChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default StatusBadge;
